import React, { useContext } from "react";
import moment from "moment";
import styled, { css } from "styled-components";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import { LanguageNameSpaces } from "@shared/types";
import { toFixed } from "@shared/utils/numbers.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import Tooltip from "antd/es/tooltip";
import { getIsApiSlotsPriceRowVisible } from "features/settings/common/apiSlotsValidation.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import { calcApiSlotsPrice } from "features/settings/Subscription/utils";
import { useUser } from "@shared/utils/hooks/misc.ts";

const ApiSlotsPriceRowComponent = () => {
  const {
    numApiSlots,
    upgradePrices,
    isStandaloneApiSlots,
    additionalSlotPrice,
    isGetUpgradePricesEnabled,
    duration,
  } = useContext(BuySubscriptionContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);

  const { totalPrice } = calcApiSlotsPrice(
    numApiSlots ?? 0,
    additionalSlotPrice
  );

  const price =
    (isGetUpgradePricesEnabled
      ? upgradePrices?.slots_setup_price
      : totalPrice * (duration / 30)) ?? 0;
  const displayedPrice = `$${toFixed(price, 2)}`;

  const tooltipContent = <TooltipBreakdown totalPrice={totalPrice} />;

  if (
    !getIsApiSlotsPriceRowVisible({
      numApiSlots,
      additionalSlotPrice,
      isStandaloneApiSlots,
    })
  )
    return null;

  return (
    <ApiSlotsPriceRow>
      <Flex>
        <Title level={4} type="medium">
          x{numApiSlots}{" "}
          {isGetUpgradePricesEnabled
            ? t("api_slots.api_slots_prorated")
            : t("api_slots.api_slot")}
        </Title>
        <Tooltip title={tooltipContent}>
          <PriceWithHint level={4} type="medium">
            {displayedPrice} <InfoIcon />
          </PriceWithHint>
        </Tooltip>
      </Flex>
    </ApiSlotsPriceRow>
  );
};

export default ApiSlotsPriceRowComponent;

export interface TooltipBreakdownProps {
  totalPrice: number;
}

export const TooltipBreakdown = ({
  totalPrice,
}: TooltipBreakdownProps): React.ReactNode => {
  const {
    numApiSlots,
    upgradePrices,
    duration,
    plan,
    additionalSlotPrice,
    isGetUpgradePricesEnabled,
    isStandaloneApiSlots,
  } = useContext(BuySubscriptionContext)!;
  const { user } = useUser();
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const isMonthly = duration === 30;
  const durationConfig = getDurationConfig(duration);
  const daysLeft = isStandaloneApiSlots
    ? user?.expiration_timestamp
      ? Math.max(
          0,
          moment.unix(user.expiration_timestamp).diff(moment(), "days")
        )
      : undefined
    : plan?.upgradePlan?.days_left;

  if (isGetUpgradePricesEnabled) {
    const { totalPrice: unitPriceTotal } = calcApiSlotsPrice(
      numApiSlots ?? 0,
      additionalSlotPrice
    );
    const unitPricePerSlot =
      (numApiSlots ?? 0) > 0 ? unitPriceTotal / (numApiSlots ?? 0) : 0;
    const pricePerSlotPerMonth = unitPricePerSlot;
    const pricePerSlotPerPeriod = unitPricePerSlot * durationConfig.multiplier;
    const proratedAmount = upgradePrices?.slots_setup_price ?? 0;

    return (
      <BreakdownContainer>
        <BreakdownSection>
          <Text type="secondary">Unit Price</Text>
          <BreakdownRow>
            <BreakdownMuted>
              ${toFixed(pricePerSlotPerMonth, 2)} / {t("api_slots.api_slot")} /{" "}
              {t("api_slots.month")}
              {!isMonthly &&
                ` ($${toFixed(pricePerSlotPerPeriod, 2)} / ${t(
                  durationConfig.translationKey
                )})`}
            </BreakdownMuted>
          </BreakdownRow>
        </BreakdownSection>
        <BreakdownDivider />
        <BreakdownSection>
          <Text type="secondary">{durationConfig.totalPriceSectionLabel}</Text>
          <BreakdownRow>
            <BreakdownMuted>
              {numApiSlots} {t("api_slots.api_slot")}s × $
              {toFixed(
                isMonthly ? pricePerSlotPerMonth : pricePerSlotPerPeriod,
                2
              )}
            </BreakdownMuted>
            <span>
              $
              {toFixed(
                (numApiSlots ?? 0) *
                  (isMonthly ? pricePerSlotPerMonth : pricePerSlotPerPeriod),
                2
              )}
            </span>
          </BreakdownRow>
        </BreakdownSection>
        {daysLeft !== undefined && (
          <>
            <BreakdownDivider />
            <BreakdownSection>
              <Text type="secondary">Prorated Price</Text>
              <BreakdownRow>
                <BreakdownMuted>
                  {t("api_slots.prorated_days", { days: daysLeft })}
                </BreakdownMuted>
                <span>${toFixed(proratedAmount, 2)}</span>
              </BreakdownRow>
            </BreakdownSection>
          </>
        )}
        <BreakdownDivider />
        <BreakdownTotalRow>
          <span>Total Due Today</span>
          <BreakdownTotalAmount>
            ${toFixed(proratedAmount, 2)}
          </BreakdownTotalAmount>
        </BreakdownTotalRow>
      </BreakdownContainer>
    );
  }

  const pricePerSlotPerMonth =
    (numApiSlots ?? 0) > 0 ? totalPrice / (numApiSlots ?? 0) : 0;
  const pricePerSlotPerPeriod =
    pricePerSlotPerMonth * durationConfig.multiplier;
  const periodTotalPrice = totalPrice * durationConfig.multiplier;

  return (
    <BreakdownContainer>
      <BreakdownSection>
        <Text type="secondary">Unit Price</Text>
        <BreakdownRow>
          <BreakdownMuted>
            ${toFixed(pricePerSlotPerMonth, 2)} / {t("api_slots.api_slot")} /{" "}
            {t("api_slots.month")}
            {!isMonthly &&
              ` ($${toFixed(pricePerSlotPerPeriod, 2)} / ${t(
                durationConfig.translationKey
              )})`}
          </BreakdownMuted>
        </BreakdownRow>
      </BreakdownSection>
      <BreakdownDivider />
      <BreakdownSection>
        <Text type="secondary">{durationConfig.totalPriceSectionLabel}</Text>
        <BreakdownRow>
          <BreakdownMuted>
            {numApiSlots} {t("api_slots.api_slot")}s × $
            {toFixed(
              isMonthly ? pricePerSlotPerMonth : pricePerSlotPerPeriod,
              2
            )}
          </BreakdownMuted>
          <span>${toFixed(periodTotalPrice, 2)}</span>
        </BreakdownRow>
      </BreakdownSection>
      <BreakdownDivider />
      <BreakdownTotalRow>
        <span>{t("api_slots.total")}</span>
        <BreakdownTotalAmount>
          ${toFixed(periodTotalPrice, 2)}
        </BreakdownTotalAmount>
      </BreakdownTotalRow>
    </BreakdownContainer>
  );
};

const DURATION_CONFIG: Record<
  number,
  {
    multiplier: number;
    translationKey: TranslationKeys<LanguageNameSpaces.BILLING>;
    priceSectionKey: TranslationKeys<LanguageNameSpaces.BILLING>;
    totalPriceSectionLabel: string;
  }
> = {
  30: {
    multiplier: 1,
    translationKey: "duration.month",
    priceSectionKey: "duration.monthly",
    totalPriceSectionLabel: "Total Monthly Price",
  },
  90: {
    multiplier: 3,
    translationKey: "duration.quarter",
    priceSectionKey: "duration.quarterly",
    totalPriceSectionLabel: "Total Quarterly Price",
  },
  180: {
    multiplier: 6,
    translationKey: "duration.half_year",
    priceSectionKey: "duration.half_yearly",
    totalPriceSectionLabel: "Total Half Yearly Price",
  },
  360: {
    multiplier: 12,
    translationKey: "duration.year",
    priceSectionKey: "duration.annually",
    totalPriceSectionLabel: "Total Annual Price",
  },
};

const getDurationConfig = (duration: number) =>
  DURATION_CONFIG[duration] ?? DURATION_CONFIG[360];

export const ApiSlotsPriceRow = styled.div`
  margin-top: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Reads like the other summary rows: a muted label and a value. */
      > div > h4:first-child {
        color: ${p.theme.colors.secondaryText};
        font-size: 13.5px;
        font-weight: ${p.theme.typography.fontWeights.NORMAL};
      }
    `}
`;
export const PriceWithHint = styled(Title)`
  cursor: help;
  border-bottom: 1px dashed currentColor;
  display: inline;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        color: ${p.theme.colors.titleColor};
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
        font-variant-numeric: tabular-nums;
      }
    `}
`;
export const InfoIcon = styled(InfoCircleOutlined)`
  font-size: 12px;
  color: inherit;
  margin-left: 4px;
`;
export const BreakdownContainer = styled.div`
  min-width: 220px;
  padding: 4px 0;
`;
export const BreakdownSection = styled.div`
  padding: 4px 0;
`;
export const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 12px;
`;
export const BreakdownMuted = styled.span``;
export const BreakdownDivider = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  margin: 4px 0;
`;
export const BreakdownTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 0;
`;
export const BreakdownTotalAmount = styled.span`
  font-weight: 700;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      font-weight: ${p.theme.typography.fontWeights.BOLD};
    `}
`;
